import { useEffect, useRef } from "react";
import type { OutboxEvent } from "@baton/contracts";
import type { DashboardApi } from "../api.js";

interface RealtimeFrame {
  type?: string;
  event?: OutboxEvent;
}

/**
 * One ticket-authenticated WebSocket per unlock. The last delivered outbox id is kept
 * so a reconnect resumes without gaps; backoff grows to 30 s with jitter and the
 * subscription is torn down on unmount or lock. Frames are routing metadata only.
 */
export function useRealtime(
  api: DashboardApi,
  onEvent: (event: OutboxEvent) => void,
  onStatus?: (status: "connected" | "reconnecting" | "unavailable", detail?: string) => void,
): void {
  const handler = useRef(onEvent);
  const status = useRef(onStatus);
  handler.current = onEvent;
  status.current = onStatus;

  useEffect(() => {
    let socket: WebSocket | null = null;
    let cancelled = false;
    let attempt = 0;
    let afterId = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const schedule = (detail?: string) => {
      if (cancelled) return;
      attempt += 1;
      const delay = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5)) * (0.7 + Math.random() * 0.6);
      status.current?.(attempt > 3 ? "unavailable" : "reconnecting", detail);
      timer = setTimeout(() => void connect(), delay);
    };

    const connect = async () => {
      if (cancelled) return;
      try {
        const ticket = await api.eventTicket(afterId);
        if (cancelled) return;
        socket = new WebSocket(api.eventUrl(ticket.websocketPath), ticket.websocketProtocol);
        socket.onopen = () => { attempt = 0; status.current?.("connected"); };
        socket.onmessage = (message) => {
          let frame: RealtimeFrame;
          try { frame = JSON.parse(String(message.data)) as RealtimeFrame; } catch { return; }
          if (frame.type !== "event" || !frame.event) return;
          afterId = Math.max(afterId, frame.event.id);
          handler.current(frame.event);
        };
        socket.onclose = () => { socket = null; schedule(); };
        socket.onerror = () => { socket?.close(); };
      } catch (caught) {
        schedule(caught instanceof Error ? caught.message : "Realtime connection failed");
      }
    };

    void connect();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      socket?.close();
    };
  }, [api]);
}
